import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import AES from 'crypto-js/aes';

import { getApiBaseUrl } from '../config';
import {
  Campaign,
  CreateCampaignDto,
  UpdateCampaignTitleDto,
  CopyCampaignDto,
  DeleteCampaignDto,
  UpdateCampaignDto,
  GameResult,
  WixContactsLabel,
  CampaignStatistics,
  Lead,
  UserAuthData,
  SignInDto,
  SignUpDto,
  RequestResetPasswordDto,
  SetNewPasswordDto,
  UpdateTzDto,
  UploadFileResult,
  UploadFileDto,
  CreatePaymentLinkDto,
  ActivateAmoCRMDto,
  ActivateAmoCRMResult,
  GetAmoCRMPipelinesDto,
  GetAmoCRMPipelinesResult,
  GetAmoCRMPipelineStatusesDto,
  GetAmoCRMPipelineStatusesResult,
  GetGetResponseListsResult,
  GetGetResponseListsDto,
  GetGetCourseGroupsResult,
  GetGetCourseGroupsDto,
} from '../types';
import { RootState } from '../lib';
import { storage } from '../lib/utils/storage';
import { CouponWithUsages, CreateCouponDto } from '../types/coupon';

const generateClientSecret = (): string => {
  const data = {
    date: new Date().toISOString(),
  };

  return AES.encrypt(JSON.stringify(data), '5cbd253c').toString();
};

export const getToken = (state: RootState): string => {
  if (state.config.platform === 'wix') {
    return state.app.wixUser?.accessToken || '';
  }

  if (state.config.platform === 'standalone') {
    return storage.getItem('accessToken') || '';
  }

  return '';
};

const getHeaders = ({
  wixInstanceId,
  userUuid,
}: {
  wixInstanceId?: string;
  userUuid?: string;
}): Record<string, string> => {
  if (wixInstanceId) {
    return { 'Wix-Instance-Id': wixInstanceId };
  }

  if (userUuid) {
    return { 'User-Uuid': userUuid };
  }

  return {};
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, api) => {
      const state = api.getState() as RootState;
      const token = getToken(state);
      const platform = state.config.platform;

      token && headers.set('X-Auth-Token', token);
      platform && headers.set('X-Site-Platform', platform);

      return headers;
    },
  }),
  tagTypes: ['user', 'campaigns', 'game-result', 'coupons'],
  endpoints: (builder) => ({
    getCampaigns: builder.query<Campaign[], void>({
      query: () => 'campaigns',
      providesTags: ['campaigns'],
    }),
    createCampaign: builder.mutation<{ id: number }, CreateCampaignDto>({
      query: (data) => ({
        url: 'campaigns',
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['campaigns'],
    }),
    updateCampaignName: builder.mutation<UpdateCampaignTitleDto, UpdateCampaignTitleDto>({
      query: ({ id, name }) => ({
        url: `campaigns/update-name/${id}`,
        body: { name },
        method: 'PATCH',
      }),
      invalidatesTags: ['campaigns'],
    }),
    deleteCampaign: builder.mutation<{ id: number }, DeleteCampaignDto>({
      query: ({ id }) => ({
        url: `campaigns/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['campaigns'],
    }),
    copyCampaign: builder.mutation<Campaign, CopyCampaignDto>({
      query: (data) => ({
        url: 'campaigns/copy-campaign',
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['campaigns'],
    }),
    updateCampaign: builder.mutation<Campaign, UpdateCampaignDto>({
      query: (data) => {
        return {
          url: `campaigns/update/${data.id}`,
          body: {
            ...data,
            campaign: {
              ...data.campaign,
              wixCoupons: data.campaign?.wixCoupons?.map((coupon) => ({
                uuid: coupon.uuid,
                description: coupon.description,
                winChance: coupon.winChance,
                imageSrc: coupon.imageSrc,
                displayName: coupon.displayName,
              })),
            },
          },
          method: 'PUT',
        };
      },
      invalidatesTags: ['campaigns'],
    }),
    publishCampaign: builder.mutation<void, { publish?: boolean; id: number; publishOnDirectLink?: boolean }>({
      query: ({ id, publish, publishOnDirectLink }) => ({
        url: `campaigns/publish/${id}`,
        method: 'PATCH',
        body: { publish, publishOnDirectLink },
      }),
      invalidatesTags: ['campaigns'],
    }),
    getCoupons: builder.query<CouponWithUsages[], void>({
      query: () => `coupons`,
      providesTags: ['coupons'],
    }),
    getGameResult: builder.query<GameResult, { campaignId: number }>({
      query: ({ campaignId }) => `campaigns/get-game-result/${campaignId}`,
      providesTags: ['game-result'],
    }),
    getCampaignStatistics: builder.query<CampaignStatistics, { campaignId: number }>({
      query: ({ campaignId }) => `campaigns/campaign-statistics/${campaignId}`,
    }),
    getCampaignLeads: builder.query<{ leads: Lead[] }, { campaignId: number }>({
      query: ({ campaignId }) => `campaigns/leads/${campaignId}`,
    }),
    getUser: builder.query<UserAuthData, void>({
      query: () => 'auth/user',
      providesTags: ['user'],
    }),
    getTodayLeadsAmount: builder.query<{ amount: number }, void>({
      query: () => `campaigns/today-leads-amount?timeZoneOffset=${-new Date().getTimezoneOffset()}`,
    }),
    getWixContactsLabels: builder.query<WixContactsLabel[], void>({
      query: () => `campaigns/wix/get-contacts-labels`,
    }),
    sendConfirmEmail: builder.mutation<void, void>({
      query: () => ({
        url: 'auth/send-email-confirm',
        method: 'POST',
      }),
    }),
    createCoupon: builder.mutation<CouponWithUsages, CreateCouponDto>({
      query: (data) => ({
        url: 'coupons',
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['coupons'],
    }),
    updateCoupon: builder.mutation<CouponWithUsages, CreateCouponDto & { uuid: string }>({
      query: (data) => ({
        url: `coupons/${data.uuid}`,
        body: data,
        method: 'PUT',
      }),
      invalidatesTags: ['coupons', 'campaigns'],
    }),
    deleteCoupon: builder.mutation<{ uuid: string }, { uuid: string }>({
      query: (data) => ({
        url: `coupons/${data.uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['coupons', 'campaigns'],
    }),
    updateTz: builder.mutation<void, UpdateTzDto>({
      query: (data) => ({
        url: `auth/update-tz`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    uploadFile: builder.mutation<UploadFileResult, UploadFileDto>({
      query: (data) => ({
        url: data.get('uploadUrl') as string,
        body: data,
        method: 'POST',
        formData: true,
      }),
    }),
    createPaymentLink: builder.mutation<{ url: string }, CreatePaymentLinkDto>({
      query: (data) => ({
        url: 'yookassa/payment-link',
        method: 'POST',
        body: data,
      }),
    }),
    sendTestLead: builder.mutation<{ ok: boolean }, { campaignId: number; integrationId: string; params?: any }>({
      query: (body) => ({
        url: `integrations/send-test-lead`,
        method: 'POST',
        body,
      }),
    }),
    activateAmoCRM: builder.mutation<ActivateAmoCRMResult, ActivateAmoCRMDto>({
      query: ({ campaignId, ...body }) => ({
        url: `integrations/amocrm/activate/${campaignId}`,
        method: 'POST',
        body,
      }),
    }),
    getAmoCRMPipelines: builder.query<GetAmoCRMPipelinesResult, GetAmoCRMPipelinesDto>({
      query: ({ campaignId }) => ({
        url: `integrations/amocrm/get-pipelines/${campaignId}`,
      }),
    }),
    getAmoCRMPipelineStatuses: builder.query<GetAmoCRMPipelineStatusesResult, GetAmoCRMPipelineStatusesDto>({
      query: ({ campaignId, pipelineId }) => ({
        url: `integrations/amocrm/get-pipeline-statuses/${campaignId}/${pipelineId}`,
      }),
    }),
    getGetResponseLists: builder.query<GetGetResponseListsResult, GetGetResponseListsDto>({
      query: ({ apiKey }) => ({
        url: `integrations/get-response/get-lists`,
        headers: { 'Get-Response-Api-Key': apiKey },
      }),
    }),
    getGetCourseGroups: builder.query<GetGetCourseGroupsResult, GetGetCourseGroupsDto>({
      query: (data) => ({
        url: `integrations/get-course/get-groups`,
        method: 'POST',
        body: data,
      }),
    }),
    // unauth requests
    getActiveCampaigns: builder.query<Campaign[], { wixInstanceId?: string; userUuid?: string }>({
      query: ({ wixInstanceId, userUuid }) => ({
        url: `campaigns-unauth/wix/get-active`,
        headers: getHeaders({ wixInstanceId, userUuid }),
      }),
    }),
    getActiveCampaign: builder.query<
      Campaign | undefined,
      { campaignId: number | string; wixInstanceId?: string; userUuid?: string; source?: string }
    >({
      query: ({ wixInstanceId, campaignId, userUuid, source }) => ({
        url: `campaigns-unauth/wix/get-active/${campaignId}`,
        params: { source },
        headers: getHeaders({ wixInstanceId, userUuid }),
      }),
    }),
    getGameResultFromSite: builder.query<
      GameResult,
      { campaignId: number; wixInstanceId?: string; userUuid?: string; data: any }
    >({
      query: ({ campaignId, wixInstanceId, data, userUuid }) => ({
        url: `campaigns-unauth/wix/get-game-result/${campaignId}`,
        headers: { 'Client-Secret': generateClientSecret(), ...getHeaders({ wixInstanceId, userUuid }) },
        method: 'POST',
        body: data,
      }),
    }),
    signIn: builder.mutation<{ accessToken: string }, SignInDto>({
      query: (body) => ({
        url: 'auth/sign-in',
        method: 'POST',
        body,
      }),
    }),
    signUp: builder.mutation<{ accessToken: string }, SignUpDto>({
      query: (body) => ({
        url: 'auth/sign-up',
        method: 'POST',
        body,
      }),
    }),
    requestResetPassword: builder.mutation<undefined, RequestResetPasswordDto>({
      query: (body) => ({
        url: 'auth/request-reset-password',
        method: 'POST',
        body,
      }),
    }),
    setNewPassword: builder.mutation<undefined, SetNewPasswordDto>({
      query: (body) => ({
        url: 'auth/set-new-password',
        method: 'POST',
        body,
      }),
    }),
  }),
});
